// Shared title-qualifier machinery — the ONE implementation of "how do we tell
// two products with the same name apart".
//
// It was written for on-page display names (`uniqueDisplayNames` in
// src/lib/productTitle.ts, which imports everything below) and round 36 reuses
// exactly the same rules for the prerendered <title>, the body <h1> and
// og:title, so head and body can never disagree.
//
// Rules encoded here (learned by breaking them once):
//  1. Exactly ONE qualifier, never stacked.
//  2. Best-first: size → pattern/finish → colour → material, then stop.
//  3. Contradiction guard on colour and dimensions, tested against BOTH the
//     built name and the raw supplier title.
//  4. Meaningless standalone tokens are rejected.
//  5. A token that is not a known size / colour / material / descriptor is a
//     supplier model name — dropped, never shown.
//  6. If nothing passes, the name is left unchanged. Two identical names beat a
//     nonsense suffix.
//
// ONE deliberate difference for head titles (`headTitleQualifiers` below):
// price is NEVER a qualifier. A price inside a <title> or <h1> goes stale the
// moment the supplier reprices, and a stale price in a search result is a false
// claim. The price lives in the meta description and the Offer schema.

export const COLORS =
  "Red|Orange|Yellow|Green|Blue|Purple|Pink|Brown|Black|White|Gray|Grey|Beige|Tan|Cream|Ivory|Charcoal|Navy|Teal|Gold|Silver|Bronze|Copper|Walnut|Espresso|Cherry|Mahogany|Oak|Maple|Ash|Wenge|Antique|Taupe|Sand|Mocha|Caramel|Burgundy|Maroon|Olive|Sage|Mint|Coral|Peach|Rust|Slate|Smoke|Pearl|Champagne|Amber|Honey|Chestnut|Gunmetal|Pewter|Nickel|Chrome|Brass|Cobalt|Indigo|Lavender|Plum|Mustard|Aqua|Turquoise|Toffee|Camel|Cognac|Terracotta|Driftwood|Khaki|Multi";
export const COLOR_PREFIXES = "Light|Dark|Medium|Matte|Glossy|Bright|Warm|Cool|Pale|Deep|Rich|Natural";

export const LEADING_COLOR_RE = new RegExp(
  `^((?:(?:${COLOR_PREFIXES})\\s+)?(?:${COLORS})(?:\\s*(?:&|\\/|,)\\s*(?:(?:${COLOR_PREFIXES})\\s+)?(?:${COLORS}))*)\\b`,
  "i",
);
export const ANY_COLOR_RE = new RegExp(`\\b((?:(?:${COLOR_PREFIXES})\\s+)?(?:${COLORS}))\\b`, "i");

// Round 39: the prefix and noun vocabularies are exported so the title
// shortener in product-meta.mjs protects materials using THIS list, rather than
// a second copy that could drift.
// Round 40, FIX 1: the vocabulary was missing the commonest supplier materials
// (polyester, plastic, suede, manufactured wood, aluminum, stainless steel …),
// so the shortener treated them as free descriptors and promoted a LATER
// material to "first material" — a plastic chair claiming to be metal.
export const MATERIAL_PREFIXES =
  "Italian|Genuine|Top[-\\s]?Grain|Full[-\\s]?Grain|Faux|Bonded|Vegan|Performance|Oil[-\\s]?Rubbed|Brushed|Polished|Antiqued|Antique|Hammered|Frosted|Smoked|Reclaimed|Woven|Solid Manufactured|Manufactured|Engineered|Imitation|Simulated|Stainless|Mirrored|Solid";
// Round 41, FIX 1. A prefix that changes what the material IS belongs to the
// material token: "Faux Leather" is not leather and "Manufactured Wood" is not
// wood. These are kept whole or removed whole — never shrunk away on their own.
// Only the provenance/finish prefixes NOT listed here may be shrunk away.
export const MEANING_PREFIXES =
  "Faux|Bonded|Vegan|Manufactured|Engineered|Imitation|Simulated|Stainless|Mirrored";
export const MATERIAL_WORDS =
  "Leather|Velvet|Bouclé|Boucle|Linen|Chenille|Microfiber|Marble|Travertine|Rattan|Wicker|Teak|Walnut|Oak|Ash|Maple|Acacia|Mahogany|Glass|Brass|Iron|Concrete|Mango Wood|Ceramic|Porcelain|Stoneware|Metal|Wood|Fabric|Crystal|Resin|Jute|Wool|Cotton|Seagrass|Bamboo|Terracotta|Stone|Chrome|Nickel|Bronze|Copper|Steel|Polypropylene|Polyester|Polyresin|Plastic|Vinyl|Acrylic|Aluminum|Aluminium|Suede|Shearling|Sheepskin|Fleece|Sherpa|Fur|Rubber|Slate|Alabaster|Silk|Satin|Viscose";


export const MATERIAL_RE = new RegExp(
  `\\b(?:(${MATERIAL_PREFIXES})\\s+)?(${MATERIAL_WORDS})\\b`,
  "i",
);

export const STYLE_DESCRIPTORS = {
  "mid century": "Mid-Century", "mid-century": "Mid-Century", "midcentury": "Mid-Century",
  "modern": "Modern", "industrial": "Industrial", "scandinavian": "Scandinavian",
  "coastal": "Coastal", "contemporary": "Contemporary", "rustic": "Rustic",
  "minimalist": "Minimalist", "tufted": "Tufted", "upholstered": "Upholstered",
  "sculptural": "Sculptural", "pedestal": "Pedestal", "floating": "Floating",
  "cane": "Cane", "farmhouse": "Farmhouse", "transitional": "Transitional",
  "art deco": "Art Deco", "japandi": "Japandi", "bohemian": "Bohemian",
};

export const ACRONYMS = new Set(["USB", "LED", "TV", "UV", "HDMI", "AC", "DC", "RGB", "IP", "BTU", "QI"]);

export function titleCase(s) {
  return String(s ?? "")
    .split(/\s+/)
    .map((w) => {
      if (!w) return w;
      if (ACRONYMS.has(w.toUpperCase())) return w.toUpperCase();
      return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
    })
    .join(" ")
    .replace(/-([a-z])/g, (_m, c) => "-" + c.toUpperCase());
}

// Pattern / style / finish words a shopper would actually see. Only these (plus
// sizes, colours and materials) may be lifted out of a handle.
export const HANDLE_DESCRIPTORS = new Set([
  "floral", "daisy", "prism", "mosaic", "pebbled", "textured", "slanted", "hardback",
  "geometric", "striped", "stripe", "chevron", "herringbone", "damask", "plaid", "checkered",
  "honeycomb", "scallop", "scalloped", "ribbed", "fluted", "hexagon", "hexagonal", "diamond",
  "subway", "penny", "arabesque", "trellis", "medallion", "botanical", "leaf", "palm",
  "abstract", "solid", "woven", "braided", "tufted", "quilted", "fringed", "beaded",
  "smooth", "frosted", "clear", "seeded", "ridged", "matte", "glossy", "brushed", "hammered",
  "round", "square", "oval", "rectangular", "tapered", "drum", "empire", "bell", "cone",
  "silo", "prismatic",
]);

// Round 37, FIX 1. Compound colours and pattern names are ATOMIC. Splitting them
// produced "Sky" on a black-and-white tile ("The Night Sky" pattern) and "Sky"
// for a "Sky Blue" rug. Either the whole phrase is used, or nothing.
export const COMPOUND_COLORS = [
  "sky blue", "navy blue", "royal blue", "baby blue", "powder blue", "dusty blue",
  "off white", "antique white", "bright white", "olive green", "forest green", "sage green",
  "mint green", "hunter green", "burnt orange", "burnt sienna", "rose gold", "antiqued gold",
  "antique brass", "antiqued brass", "brushed nickel", "oil rubbed bronze", "gun metal",
  "light gray", "light grey", "dark gray", "dark grey", "charcoal gray", "charcoal grey",
  "blush pink", "dusty rose", "deep red", "wine red", "cherry red", "lemon yellow",
];

export const PATTERN_PHRASES = [
  "night sky", "mosaic tile", "herringbone weave", "star burst", "sun burst",
  "art deco", "mid century", "greek key", "chevron stripe", "polka dot", "animal print",
];

/** Every atomic phrase, longest first so "sky blue" wins over "blue". */
export const ATOMIC_PHRASES = [...COMPOUND_COLORS, ...PATTERN_PHRASES].sort(
  (a, b) => b.length - a.length,
);

/** Words that only ever appear inside an atomic phrase — never alone. */
export const COMPOUND_FRAGMENTS = new Set(
  ATOMIC_PHRASES.flatMap((p) => p.split(" ")).filter(
    (w) => !new RegExp(`^(?:${COLORS})$`, "i").test(w),
  ),
);

export const LEFTOVER_STOPWORDS = new Set([
  "with", "and", "the", "for", "in", "of", "a", "an", "by", "from", "set", "pair",
  "inch", "inches", "cm", "ft", "feet", "piece", "pieces", "pc", "pcs", "style", "new",
]);

// Words that carry no distinguishing meaning when they stand alone.
export const MEANINGLESS_QUALIFIERS = new Set([
  "throw", "light", "lights", "lamp", "glass", "clear", "metal", "wood", "set", "sets",
  "piece", "pieces", "pcs", "style", "new", "size", "one", "two", "three", "four", "five",
  "six", "seven", "eight", "nine", "ten", "single", "double",
  // What is left of "Two Panel" / "Single Door" once its count is gone.
  "panel", "panels", "door", "doors", "drawer", "drawers", "tier", "tiers", "position",
]);


/** A human size label: dimensions ("2' x 3'", "24 x 36 in") or a size word. */
export function extractSizeLabel(rawTitle) {
  const t = rawTitle || "";
  const dim = t.match(
    /\b(\d+(?:\.\d+)?)\s*(?:'|ft\.?|feet|"|in\.?|inch(?:es)?|cm)?\s*[xX×]\s*(\d+(?:\.\d+)?)\s*('|ft\.?|feet|"|in\.?|inch(?:es)?|cm)?/,
  );
  if (dim) {
    const unit = (dim[3] || "").replace(/\.$/, "").toLowerCase();
    const suffix = unit === "'" || unit === "ft" || unit === "feet" ? "'" : unit === "cm" ? "cm" : '"';
    return `${dim[1]}${suffix} x ${dim[2]}${suffix}`;
  }
  const word = t.match(/\b(Twin XL|Twin|Full|Queen|King|California King|Runner|Round|Square)\b/i);
  return word ? titleCase(word[1]) : null;
}

/** A single measurement from a title, e.g. `24"`, `30 Inch`, `120cm`. */
export function extractSingleMeasure(rawTitle) {
  const m = (rawTitle || "").match(/\b(\d{1,3}(?:\.\d)?)\s*(?:"|''|'(?!s\b)|(?:in\.?|inch(?:es)?|ft\.?|feet|cm)\b)/i);
  if (!m) return null;
  const unit = m[0].slice(m[1].length).trim().toLowerCase().replace(/\.$/, "");
  const suffix = unit === "cm" ? "cm" : unit === "'" || unit === "ft" || unit === "feet" ? "'" : '"';
  return `${m[1]}${suffix}`;
}

/** ft/feet → `'`, in/inch → `"`, cm → `cm`, anything else → "" (unknown). */
export function normaliseUnit(raw) {
  const u = String(raw ?? "").trim().toLowerCase().replace(/\.$/, "");
  if (u === "'" || u === "ft" || u === "feet" || u === "foot") return "'";
  if (u === '"' || u === "''" || u === "in" || u === "inch" || u === "inches") return '"';
  if (u === "cm") return "cm";
  return "";
}

/**
 * `17-inch-...` → `17"`, `11x14-...` → `11x14"`, `10-ft-...` → `10'`.
 * Round 38, FIX 1: a BARE leading number carries no unit, and guessing inches
 * produced `10' … Runner Rug — 10"`. A unit is only used when the handle states
 * it, or when the raw title already states one — otherwise no size qualifier.
 */
export function handleLeadingSize(handle, rawTitle = "") {
  const h = handle || "";
  const m =
    h.match(/^(\d{1,3})x(\d{1,3})-?(in|inch|inches|ft|feet|cm)?\b/) ||
    h.match(/^(\d{1,3})-(in|inch|inches|ft|feet|cm)?/);
  if (!m) return null;
  const pair = /^\d{1,3}x\d{1,3}/.test(h);
  const explicit = normaliseUnit(pair ? m[3] : m[2]);
  const unit = explicit || normaliseUnit((extractSingleMeasure(rawTitle) || "").replace(/[\d.]/g, ""));
  if (!unit) return null; // never infer a unit
  return pair ? `${m[1]}x${m[2]}${unit}` : `${m[1]}${unit}`;
}

/** Every measurement stated in a string as `{value, unit}` ("" unit = unknown). */
export function measuresOf(s) {
  const out = [];
  const text = s || "";
  const unit = `(?:"|''|in\\.?|inch(?:es)?|'|ft\\.?|feet|cm)`;
  const pair = new RegExp(`(\\d+(?:\\.\\d+)?)\\s*(${unit})?\\s*[xX×]\\s*(\\d+(?:\\.\\d+)?)\\s*(${unit})?`, "g");
  let m;
  const seenPair = new Set();
  while ((m = pair.exec(text))) {
    const u = normaliseUnit(m[4]) || normaliseUnit(m[2]);
    out.push({ value: `${m[1]}x${m[3]}`, unit: u });
    seenPair.add(m[1]);
    seenPair.add(m[3]);
  }
  const single = new RegExp(`\\b(\\d+(?:\\.\\d+)?)\\s*(${unit})`, "g");
  while ((m = single.exec(text))) out.push({ value: m[1], unit: normaliseUnit(m[2]) });
  return out;
}

/** Every dimension stated in a string, normalised (`7" X 7"` → `7x7`, `10"` → `10`). */
export function normalisedDimensions(s) {
  return new Set(measuresOf(s).map((m) => m.value));
}


/** Colour words stated anywhere in a string, including compound colours. */
export function statedColors(s) {
  const text = ` ${String(s ?? "").toLowerCase()} `;
  const out = new Set();
  for (const c of COMPOUND_COLORS) if (text.includes(` ${c} `) || text.includes(`${c} `)) out.add(c);
  const re = new RegExp(`\\b(?:${COLORS})\\b`, "gi");
  let m;
  while ((m = re.exec(text))) out.add(m[0].toLowerCase());
  return out;
}

/** Bed sizes stated in a string ("california king" is not "king"). */
export function bedSizesOf(s) {
  return new Set(String(s ?? "").toLowerCase().match(/\b(?:twin xl|twin|full|queen|california king|king)\b/g) ?? []);
}

/** True when a qualifier asserts a colour or a dimension the name contradicts. */
export function contradictsName(name, qualifier) {
  const cm = qualifier.match(ANY_COLOR_RE);
  if (cm && ANY_COLOR_RE.test(name) && !name.toLowerCase().includes(cm[1].toLowerCase())) return true;

  // Round 37, FIX 1: any colour-like qualifier is rejected when the name already
  // states a different colour ("The Night Sky" pattern on a black-and-white tile).
  const qColors = statedColors(qualifier);
  if (qColors.size) {
    const nameColors = statedColors(name);
    if (nameColors.size) {
      let shared = false;
      for (const c of qColors) if (nameColors.has(c) || [...nameColors].some((n) => c.includes(n))) shared = true;
      if (!shared) return true;
    }
  }

  // A bed size the title settles: "Split California King … — Twin" contradicts itself.
  const qBeds = bedSizesOf(qualifier);
  if (qBeds.size) {
    const nBeds = bedSizesOf(name);
    if (nBeds.size && ![...qBeds].some((b) => nBeds.has(b))) return true;
  }

  // Round 38, FIX 1: compare (value, unit) pairs. A different value contradicts,
  // and so does the SAME value in a different explicit unit (10' vs 10").
  const nameMeasures = measuresOf(name);
  if (nameMeasures.length) {
    const nameValues = new Set(nameMeasures.map((m) => m.value));
    for (const q of measuresOf(qualifier)) {
      if (!nameValues.has(q.value)) return true;
      const sameValue = nameMeasures.filter((m) => m.value === q.value);
      if (q.unit && sameValue.every((m) => m.unit && m.unit !== q.unit)) return true;
    }
  }
  return false;
}

// Round 42. Supplier handles are STALE — the supplier reuses listings, so a
// handle often describes a product that no longer exists (`19-faux-fur-mirror-…`
// on a brass mirror). A handle-derived qualifier is therefore only accepted when
// the raw supplier title (or the product type) states the same word(s); a
// handle-derived size must match a measurement the title actually states.
function supportWords(s) {
  return ` ${String(s ?? "").toLowerCase().replace(/[^a-z0-9"'.]+/g, " ").replace(/\s+/g, " ")} `;
}

export function qualifierSupportedByTitle(qualifier, rawTitle, productType = "") {
  const q = String(qualifier ?? "").trim();
  if (!q) return false;

  const asMeasures = measuresOf(q);
  if (asMeasures.length) {
    const stated = measuresOf(rawTitle || "");
    return asMeasures.every((m) =>
      stated.some((s) => s.value === m.value && (!m.unit || !s.unit || s.unit === m.unit)),
    );
  }

  const text = supportWords(`${rawTitle ?? ""} ${productType ?? ""}`);
  const words = q.toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);
  if (!words.length) return false;
  return words.every((w) => {
    const forms = new Set([w, `${w}s`, `${w}es`, w.replace(/s$/, ""), w.replace(/es$/, "")]);
    for (const f of forms) if (f && text.includes(` ${f} `)) return true;
    return false;
  });
}

/**
 * A qualifier is only usable if it adds real, non-contradictory information.
 * `quoted`: the qualifier is a phrase lifted whole from the supplier title with
 * its own noun ("White Shade" on a brown lamp). It names a part, so the colour
 * guard — written for bare colours — does not apply to it.
 */
export function isUsableQualifier(name, qualifier, rawTitle = "", { quoted = false } = {}) {
  const q = (qualifier || "").trim();
  if (!q) return false;
  const words = q.split(/\s+/);
  if (words.length === 1 && MEANINGLESS_QUALIFIERS.has(q.toLowerCase().replace(/[^a-z]/g, ""))) return false;
  // Round 37, FIX 1: never a bare fragment of a compound colour or pattern name.
  if (words.length === 1 && COMPOUND_FRAGMENTS.has(q.toLowerCase().replace(/[^a-z]/g, ""))) return false;
  // Round 41, FIX 1/FIX 3: a lone material PREFIX ("Solid", "Woven", "Faux") is
  // not a material, and a lone material word whose only appearance in the
  // supplier title carries a meaning-bearing prefix ("Stainless Steel" → "Steel")
  // would state a different material. Neither may ever be a qualifier.
  if (words.length === 1) {
    const bare = q.toLowerCase().replace(/[^a-z]/g, "");
    if (new RegExp(`^(?:${MATERIAL_PREFIXES})$`, "i").test(bare)) return false;
    if (
      rawTitle &&
      new RegExp(`^(?:${MATERIAL_WORDS})$`, "i").test(bare) &&
      new RegExp(`\\b(?:${MEANING_PREFIXES})\\s+${bare}\\b`, "i").test(rawTitle) &&
      !new RegExp(`(?<!\\b(?:${MEANING_PREFIXES})\\s)\\b${bare}\\b`, "i").test(rawTitle)
    ) {
      return false;
    }
  }
  if (name.toLowerCase().includes(q.toLowerCase())) return false;
  // A bare number ("12") says nothing; a size carries its unit.
  if (!/[a-z]/i.test(q) && !measuresOf(q).some((m) => m.unit)) return false;
  // A bare size the name already states (`20"X20"` vs `20" X 20"`) adds nothing.
  const sizes = measuresOf(q);
  if (sizes.length && !/[a-z]/i.test(q.replace(/\b(?:x|in|inch|inches|ft|feet|cm)\b/gi, ""))) {
    const stated = normalisedDimensions(name);
    if (sizes.every((m) => stated.has(m.value))) return false;
  }
  if (quoted) return true;
  if (contradictsName(name, q)) return false;
  if (rawTitle && contradictsName(rawTitle, q)) return false;
  return true;
}

/**
 * Qualifiers derived from a product handle, grouped by class so the caller picks
 * exactly ONE, best-first. Never rewrites the name — the caller only appends.
 * Unknown tokens (supplier model names like "Orella") are dropped: rule 5.
 * Round 37: compound colours and pattern names are matched atomically FIRST, and
 * their words are consumed so no single-word fragment can escape.
 */
export function handleQualifiers(handle, name, rawTitle = "", productType = "", { requireSupport = true } = {}) {
  const nameLower = ` ${String(name).toLowerCase()} `;
  const nameHasColor = ANY_COLOR_RE.test(name);

  const size = handleLeadingSize(handle, rawTitle);
  const sizes = size && !nameLower.includes(size.toLowerCase()) ? [size] : [];

  const descriptors = [];
  const materials = [];
  const colors = [];

  // Atomic phrases first.
  const handleWords = String(handle || "").split("-");
  const handleText = handleWords.join(" ").toLowerCase();
  const consumed = new Set();
  for (const phrase of ATOMIC_PHRASES) {
    if (!new RegExp(`\\b${phrase}\\b`).test(handleText)) continue;
    for (const w of phrase.split(" ")) consumed.add(w);
    if (nameLower.includes(phrase)) continue;
    const label = titleCase(phrase);
    if (PATTERN_PHRASES.includes(phrase)) descriptors.push(label);
    else if (!nameHasColor) colors.push(label);
  }

  const seen = new Set();
  for (const raw of handleWords) {
    const t = raw.toLowerCase();
    if (t.length < 3 || seen.has(t) || consumed.has(t)) continue;
    if (/^\d+$/.test(t)) continue;
    if (LEFTOVER_STOPWORDS.has(t)) continue;
    if (nameLower.includes(t)) continue; // already visible in the name
    seen.add(t);

    if (HANDLE_DESCRIPTORS.has(t) || STYLE_DESCRIPTORS[t]) {
      descriptors.push(STYLE_DESCRIPTORS[t] || titleCase(t));
      continue;
    }
    if (ANY_COLOR_RE.test(t)) {
      if (!nameHasColor) colors.push(titleCase(t)); // never contradict a stated colour
      continue;
    }
    if (MATERIAL_RE.test(t)) materials.push(titleCase(t));
    // anything else: supplier model name — dropped.
  }

  // Round 42: nothing derived from the (stale) handle survives unless the raw
  // title or the product type states it too.
  const supported = (q) => !requireSupport || qualifierSupportedByTitle(q, rawTitle, productType);
  return {
    sizes: sizes.filter(supported),
    descriptors: descriptors.filter(supported),
    colors: colors.filter(supported),
    materials: materials.filter(supported),
  };
}


/**
 * ONE qualifier for a head title, best-first, or null.
 * `name` is the built page title (suffix already removed), `rawTitle` the
 * supplier title. Never price. Never stacked.
 * `dropped` (optional) is what the title builder cut from this very title —
 * { phrase, features, descriptors, materials }, see droppedTitlePhrases in
 * product-meta.mjs. Quoting the title beats guessing from the handle.
 * `accepts` (optional) is one more test a candidate must pass.
 */
export function headQualifier(handle, name, rawTitle, taken, fits, productType = "", dropped = null, accepts = null) {
  // A supplier title that carries its own dash would end up with two.
  if (String(name).includes("\u2014")) return null;
  const fromHandle = handleQualifiers(handle, name, rawTitle, productType);
  // When the name states a full dimension pair (7" x 7"), a lone measure is a
  // fragment of it and only confuses the shopper.
  const nameHasPair = /\d\s*(?:"|''|in\.?|inch(?:es)?|'|ft\.?|feet|cm)?\s*[xX×]\s*\d/.test(`${name} ${rawTitle}`);
  // A colour or material the title only states inside its accessory clause
  // belongs to a part ("With White Ceramic Sink"). Bare, it would read as the
  // product's own — the clause phrases carry it with its noun instead.
  const productPhrase = dropped?.phrase ?? String(rawTitle ?? "").split(/,|\s(?:with|featuring|including)\s/i)[0];
  const own = (q) => qualifierSupportedByTitle(q, productPhrase, productType);

  const ordered = [
    // 1. size
    extractSizeLabel(rawTitle),
    nameHasPair ? null : extractSingleMeasure(rawTitle),
    ...(nameHasPair ? [] : fromHandle.sizes),
    // 2. pattern / finish / feature
    ...(dropped?.features ?? []).map((q) => ({ q, quoted: true })),
    ...fromHandle.descriptors,
    ...(dropped?.descriptors ?? []),
    // 3. colour
    ...fromHandle.colors.filter(own),
    // 4. material
    ...fromHandle.materials.filter(own),
    ...(dropped?.materials ?? []),
  ];

  // Two rounds: a qualifier that fits beside the name as built wins over one the
  // composer has to shorten the name for (`fits` gets `asBuilt` as its 4th argument).
  for (const asBuilt of fits ? [true, false] : [false]) {
    for (const entry of ordered) {
      if (!entry) continue;
      const quoted = typeof entry === "object";
      const q = titleCase(String(quoted ? entry.q : entry).trim());
      if (!isUsableQualifier(name, q, rawTitle, { quoted })) continue;
      if (accepts && !accepts(q)) continue;
      if (taken && taken.has(`${name} — ${q}`.toLowerCase())) continue;
      // Round 38: a candidate the composer would drop at the 65-char cap is not a
      // qualifier at all — fall through to the next class instead of recording it.
      if (fits && !fits(rawTitle, q, productType, asBuilt)) continue;
      return q;
    }
  }
  return null;
}

/**
 * handle → qualifier for every indexable product that shares its built title
 * with another indexable product. Rows: { handle, rawTitle, name, productType }.
 * `droppedOf(row)` (optional) returns what the title builder cut from that row.
 *
 * Two passes per group of namesakes:
 *  1. Namesakes whose SUPPLIER titles differ are told apart by what differs
 *     ("with Four Doors" against "with Two Doors"), and every product with the
 *     same supplier title gets the same words. One of six four-door bookcases
 *     carrying "— Four Doors" while five did not said nothing true about them.
 *  2. Round 36, unchanged: among titles still identical the first keeps its
 *     title and the rest get at most one qualifier, none when nothing honest
 *     passes. A title never gets a second qualifier.
 */
export function headTitleQualifiers(rows, fits, droppedOf) {
  const groups = new Map();
  for (const r of rows) {
    const key = r.name.toLowerCase();
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(r);
  }

  const qualifiers = {};
  const stats = { duplicateBefore: 0, duplicateAfter: 0, qualified: 0, unqualifiedGroups: [] };
  const titleOf = (m) => (qualifiers[m.handle] ? `${m.name} — ${qualifiers[m.handle]}` : m.name).toLowerCase();

  for (const [, group] of groups) {
    if (group.length === 1) continue;
    stats.duplicateBefore += group.length;

    const members = [...group].sort((a, b) => a.handle.localeCompare(b.handle));

    const bySupplierTitle = new Map();
    for (const m of members) {
      const key = String(m.rawTitle).trim().toLowerCase();
      if (!bySupplierTitle.has(key)) bySupplierTitle.set(key, []);
      bySupplierTitle.get(key).push(m);
    }
    if (bySupplierTitle.size > 1) {
      const labels = new Set();
      for (const [key, same] of bySupplierTitle) {
        const others = [...bySupplierTitle.keys()].filter((k) => k !== key);
        // Only words some namesake's title lacks tell the two apart.
        const tellsApart = (q) => others.some((t) => !t.includes(q.toLowerCase()));
        const m = same[0];
        const q = headQualifier(m.handle, m.name, m.rawTitle, labels, fits, m.productType ?? "", droppedOf?.(m) ?? null, tellsApart);
        if (!q) continue;
        labels.add(`${m.name} — ${q}`.toLowerCase());
        for (const each of same) qualifiers[each.handle] = q;
      }
    }

    const taken = new Set();
    for (const m of members) {
      if (!taken.has(titleOf(m))) {
        taken.add(titleOf(m));
        continue;
      }
      if (qualifiers[m.handle]) continue;
      // Only the product phrase is passed on: a part's material must stay with
      // the part here too ("Candle In White Resin Novelty Holder" is not a resin
      // candle), while what the shortener cut is the same for every namesake.
      const phrase = droppedOf?.(m)?.phrase;
      const q = headQualifier(m.handle, m.name, m.rawTitle, taken, fits, m.productType ?? "", phrase ? { phrase } : null);
      if (!q) continue;
      qualifiers[m.handle] = q;
      taken.add(titleOf(m));
    }

    const counts = new Map();
    for (const m of members) counts.set(titleOf(m), (counts.get(titleOf(m)) ?? 0) + 1);
    for (const m of members) {
      const n = counts.get(titleOf(m)) ?? 0;
      if (n < 2) continue;
      counts.delete(titleOf(m));
      stats.duplicateAfter += n;
      stats.unqualifiedGroups.push({ name: qualifiers[m.handle] ? `${m.name} — ${qualifiers[m.handle]}` : m.name, count: n });
    }
  }

  stats.qualified = Object.keys(qualifiers).length;
  stats.unqualifiedGroups.sort((a, b) => b.count - a.count);
  return { qualifiers, stats };
}
